// Ce que montrent les drills (D-098) : les signaux en SVG (flèche et formes de Playbook BCL), les
// vignettes des cartes (flèche, pastilles de couleurs, prénom, frise du fractionné, étapes du circuit),
// la pastille de mode. Partagé par la liste, les éditeurs, la séance et le plein écran.
import {
  ARROW_DEG, KIND_ICON, KIND_LABEL, STAGE_COLOR, colorHex, intervalTimeline, isNumberPick, readCircuit, readInterval, readReaction, stepAvgMs,
  type Arrow, type Cue, type DrillKind, type IntervalConfig, type Shape,
} from '../domain/drills';
import { signSvg } from '../domain/signs';
import './drill-seq.css';
import { html, raw, type Html } from './dom';

export function arrowSvg(dir: Arrow, cls = ''): Html {
  return raw(`<svg class="${cls}" viewBox="0 0 100 100" style="transform:rotate(${ARROW_DEG[dir] ?? 0}deg)" aria-hidden="true"><path d="M50 6 L90 54 L64 54 L64 94 L36 94 L36 54 L10 54 Z" fill="#fff"/></svg>`);
}
const POLY: Record<Exclude<Shape, 'circle'>, string> = {
  triangle: '50,6 94,90 6,90', square: '8,8 92,8 92,92 8,92', diamond: '50,4 96,50 50,96 4,50', pentagon: '50,5 95,40 77,95 23,95 5,40',
  hexagon: '25,6 75,6 97,50 75,94 25,94 3,50', octagon: '31,4 69,4 96,31 96,69 69,96 31,96 4,69 4,31',
  star: '50,3 61,38 98,38 68,60 79,96 50,73 21,96 32,60 2,38 39,38',
};
export function shapeSvg(shape: Shape, cls = ''): Html {
  return raw(shape === 'circle'
    ? `<svg class="${cls}" viewBox="0 0 100 100" aria-hidden="true"><circle cx="50" cy="50" r="46" fill="#fff"/></svg>`
    : `<svg class="${cls}" viewBox="0 0 100 100" aria-hidden="true"><polygon points="${POLY[shape]}" fill="#fff"/></svg>`);
}
export const imgSrc = (data: string) => `data:image/jpeg;base64,${data}`;

/** Un signal, en grand (plein écran) ou en petit (aperçu de l'éditeur). */
export function cueHtml(cue: Cue | null, images: Map<string, string>, small = false): Html {
  if (!cue) return html``;
  switch (cue.type) {
    case 'arrow': return arrowSvg(cue.value, small ? '' : 'arrow');
    case 'shape': return shapeSvg(cue.value, small ? '' : 'arrow');
    case 'color': return html`<i class="${small ? 'c' : 'disc'}" style="background:${cue.hex};${small ? '' : `box-shadow:0 0 60px ${cue.hex}55`}"></i>`;
    case 'number': case 'letter': return html`<span class="${small ? 'ch' : 'glyph'}">${String(cue.value)}</span>`;
    case 'image': { const d = images.get(cue.value); return d ? html`<img src="${imgSrc(d)}" alt="">` : html``; }
    case 'player': return html`<span class="${small ? 'nm' : 'name'}">${cue.name}</span>`;
    case 'sign': return signCueHtml(cue, small);
  }
}

/** Un signe de système (D-161) : en petit, le pictogramme ; en grand (maquette d5), lisible à 6 m sur une carte claire, ses
 *  deux images en alternance s'il bouge, et le nom du système qui apparaît après 1 s (drill-seq.css), s'il est demandé. */
export function signCueHtml(cue: Extract<Cue, { type: 'sign' }>, small = false): Html {
  if (small) return raw(signSvg(cue.value, { left: cue.left }));
  return html`<div class="dq-sign"><div class="dq-bigsign">${raw(signSvg(cue.value, { bare: true, left: cue.left, viewBox: '15 8 70 92', animate: true, cls: 'sgn sgn-big' }))}</div>
    ${cue.name ? html`<div class="dq-signname"><b>${cue.name}</b>${cue.name !== cue.label ? html`<small>${cue.label}</small>` : ''}</div>` : ''}</div>`;
}

export const modePill = (kind: DrillKind) => html`<span class="dr-mode dr-m-${kind}">${KIND_ICON[kind]} ${KIND_LABEL[kind]}</span>`;

const W = '#E8590C', R = '#2F6DD8', G = '#9AA1AA';
/** La frise d'un fractionné : effort, récup, échauffement et retour au calme, à l'échelle. */
export function intervalFrise(c: IntervalConfig, cls = 'dr-frise', colors = { work: W, rest: R, other: G }): Html {
  const tl = intervalTimeline(c);
  return html`<div class="${cls}" aria-hidden="true">${tl.map(p => html`<i style="flex:${p.ms};background:${p.type === 'work' ? colors.work : p.type === 'rest' ? colors.rest : colors.other}"></i>`)}</div>`;
}

/** La vignette d'un drill (cartes, listes). */
export function drillThumb(d: { kind: DrillKind; config: unknown }, opts: { images?: Map<string, string>; name?: string; cls?: string } = {}): Html {
  const cls = `dr-th ${opts.cls ?? ''}`;
  if (d.kind === 'fractionne') {
    const c = readInterval(d.config);
    const n = Math.min(6, Math.max(1, c.cycles));
    return html`<span class="${cls}"><span class="fr">${Array.from({ length: n }, () => html`<i style="flex:${c.work_s};background:${W}"></i><i style="flex:${Math.max(1, c.rest_s)};background:${R}"></i>`)}</span></span>`;
  }
  if (d.kind === 'circuit') {
    const st = readCircuit(d.config).stages.slice(0, 4);
    const widths = [30, 22, 26, 18];
    return html`<span class="${cls}"><span class="st">${(st.length ? st.map(s => s.type) : ['counter', 'countdown', 'signals'] as const).map((t, i) => html`<i style="background:${STAGE_COLOR[t]};width:${widths[i]}px"></i>`)}</span></span>`;
  }
  const r = readReaction(d.config), s = r.stimuli;
  // En séquence (D-161) : la frise d'un tour, en miniature (son violet, attente en pointillé, signal noir).
  if (r.mode === 'sequence' && r.sequence) return html`<span class="${cls}"><span class="dq-thfr">${r.sequence.steps.slice(0, 6).map(x => html`<i class="${x.kind}" style="flex:${Math.max(0.4, stepAvgMs(x) / 1000)}"></i>`)}</span></span>`;
  if (s.signs) return html`<span class="${cls} dq-thsign">${raw(signSvg(s.signs === 'team' ? 'corne' : s.signs[0]!))}</span>`;
  if (s.arrows?.length) return html`<span class="${cls}">${arrowSvg(s.arrows[0]!)}</span>`;
  if (s.colors?.length) return html`<span class="${cls}"><span class="dots">${s.colors.slice(0, 4).map(c => html`<i style="background:${colorHex(c)}"></i>`)}</span></span>`;
  if (s.players) return html`<span class="${cls}">${opts.name ? html`<span class="nm">${opts.name.slice(0, 6)}</span>` : html`<span class="ti ti-people" aria-hidden="true"></span>`}</span>`;
  const img = s.images?.map(id => opts.images?.get(id)).find(Boolean);
  if (img) return html`<span class="${cls}"><img src="${imgSrc(img)}" alt=""></span>`;
  if (s.shapes?.length) return html`<span class="${cls}">${shapeSvg(s.shapes[0]!)}</span>`;
  if (s.numbers) return html`<span class="${cls}"><span class="ch">${isNumberPick(s.numbers) ? s.numbers.selected[0] : s.numbers.min}</span></span>`;
  if (s.letters) return html`<span class="${cls}"><span class="ch">${s.letters.mode === 'select' ? s.letters.selected[0] ?? 'A' : s.letters.min}</span></span>`;
  return html`<span class="${cls}">${arrowSvg('up')}</span>`;
}

